import Link from "next/link";
import { useRouter } from "next/router";

import { Button } from "@chef/components";
import { BRAND_PATHS } from "@chef/constants";
import { ThumbsUp } from "@chef/icons/symbol";
import {
  CompletedWithAllergenWarning,
  FlexWrapper,
  PageLoader,
  PreferencesDetails,
} from "@chef/smart-components";
import { useBillingQuery } from "@chef/state-management";
import {
  isConceptPreference,
  isMealboxBasket,
  isMealboxBasketType,
  isTastePreference,
} from "@chef/helpers";
import { getAttribute } from "@chef/state-management/helpers";
import { usePreselector } from "@chef/state-management/hooks";
import { useEffectOnce } from "@chef/hooks";

import { intl } from "./PreferencesSuccessPage.Intl";

export const PreferencesSuccessPage = () => {
  const router = useRouter();
  const { data: billingQuery } = useBillingQuery();

  const { expire } = usePreselector();

  useEffectOnce(() => {
    expire();
  }, router.isReady);

  if (!billingQuery) {
    return <PageLoader wholeScreen />;
  }

  const conceptPreferences =
    billingQuery.billing.preferences.filter(isConceptPreference);

  const tastePreferences =
    billingQuery.billing.preferences.filter(isTastePreference);

  const mealboxVariation = billingQuery.billing.baskets
    .find(isMealboxBasketType)
    ?.basketProducts.find(isMealboxBasket)?.variation;

  if (!mealboxVariation) {
    return null;
  }

  const portions = getAttribute("Portions", mealboxVariation);
  const meals = getAttribute("Meals", mealboxVariation);

  return (
    <FlexWrapper
      back={{ type: "hidden" }}
      close={{ type: "hidden" }}
      background="white"
      className="min-h-screen md:min-h-[unset] md:my-16"
    >
      <div
        className="flex flex-col gap-6 p-4 pt-12 text-center md:p-20"
        id="success-page-card"
      >
        <div className="flex items-center justify-center w-20 h-20 mx-auto rounded-full bg-grey-3">
          <ThumbsUp className="w-full h-full" />
        </div>

        <h1 className="text-xl md:text-2xl">
          <strong>{intl.YOUR_PREFERENCES_ARE_UPDATED}</strong>
        </h1>

        <CompletedWithAllergenWarning />

        <p>{intl.YOUR_PREFERENCES_ARE_UPDATED_SUBTITLE}</p>

        <div className="text-sm">
          <p className="mb-2">
            <strong>{intl.YOUR_CHOICE}</strong> {portions} {intl.PORTIONS},{" "}
            {meals} {intl.MEALS}.
          </p>

          <PreferencesDetails
            conceptPreferences={conceptPreferences}
            tastePreferences={tastePreferences}
          />
        </div>

        <Link href={BRAND_PATHS.MY_MENU_PAGE}>
          <Button primary>{intl.BACK_TO_MY_MENU}</Button>
        </Link>

        <Link href={BRAND_PATHS.MEALBOX_PREFERENCE} className="underline">
          <strong className="text-sm">{intl.CHANGE_YOUR_PREFERENCES}</strong>
        </Link>
      </div>
    </FlexWrapper>
  );
};
